/**
 * Shared paste-import session for a foundation run.
 * Survives modal close so background imports stay discoverable via banner + reopen.
 */

import {
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useRef,
    useState,
    type FC,
    type ReactNode,
} from 'react'
import { useChannel } from 'ably/react'
import toast from 'react-hot-toast'
import {
    foundationPasteImportService,
    type FoundationPasteImportProgress,
} from '@/services/persuasion-lab/foundation-paste-import'
import {
    FoundationPasteImportContext,
    isActivePasteImportStatus,
    type FoundationPasteImportContextValue,
    type PasteImportTab,
} from './foundation-paste-import-context.shared'

export const FoundationPasteImportProvider: FC<{
    runId: string
    onImportComplete?: () => void
    children: ReactNode
}> = ({ runId, onImportComplete, children }) => {
    const [isOpen, setIsOpen] = useState(false)
    const [initialTab, setInitialTab] = useState<PasteImportTab>('guide')
    const [jobId, setJobId] = useState<string | null>(null)
    const [progress, setProgress] = useState<FoundationPasteImportProgress | null>(null)
    const [estimatedCredits, setEstimatedCredits] = useState<number | null>(null)
    const [bannerDismissed, setBannerDismissed] = useState(false)
    const completedNotifiedRef = useRef<string | null>(null)

    const syncFromRunMetadata = useCallback(
        (run: { metadata?: { foundation_paste_import?: FoundationPasteImportProgress } } | null) => {
            const stored = run?.metadata?.foundation_paste_import
            if (!stored?.jobId) return
            setJobId(stored.jobId)
            setProgress(stored)
            if (isActivePasteImportStatus(stored.status)) {
                setBannerDismissed(false)
            }
        },
        []
    )

    const refreshActiveImport = useCallback(async () => {
        if (!runId) return
        try {
            const { progress: stored } = await foundationPasteImportService.getActiveImport(runId)
            if (!stored?.jobId) return
            setJobId(stored.jobId)
            setProgress(stored)
            if (isActivePasteImportStatus(stored.status)) {
                setBannerDismissed(false)
            }
        } catch {
            // Non-fatal — Ably may still deliver updates
        }
    }, [runId])

    useEffect(() => {
        if (!runId) return
        refreshActiveImport()
    }, [runId, refreshActiveImport])

    useEffect(() => {
        if (!runId || !jobId || !isActivePasteImportStatus(progress?.status)) return
        const timer = setInterval(() => {
            foundationPasteImportService
                .getImportStatus(runId, jobId)
                .then(({ progress: latest }) => {
                    if (!latest) return
                    setProgress(latest)
                    if (!isActivePasteImportStatus(latest.status)) {
                        onImportComplete?.()
                    }
                })
                .catch(() => {})
        }, 8000)
        return () => clearInterval(timer)
    }, [runId, jobId, progress?.status, onImportComplete])

    useChannel(`run:${runId}`, message => {
        const data = message?.data as { jobId?: string; step?: string; label?: string; detail?: string; percent?: number; error?: string; summary?: FoundationPasteImportProgress['summary']; warnings?: string[] } | undefined
        if (!data?.jobId || (jobId && data.jobId !== jobId)) return

        if (message.name === 'foundation-import-progress') {
            setJobId(data.jobId!)
            setProgress(prev => ({
                ...(prev || { jobId: data.jobId!, status: 'running' }),
                status: 'running',
                step: data.step,
                label: data.label,
                detail: data.detail,
                percent: data.percent,
            }))
            setBannerDismissed(false)
        } else if (message.name === 'foundation-import-complete') {
            setJobId(data.jobId!)
            setProgress(prev => ({
                ...(prev || { jobId: data.jobId!, status: 'complete' }),
                status: 'complete',
                label: 'Import complete',
                percent: 100,
                summary: data.summary,
                warnings: data.warnings,
            }))
            if (completedNotifiedRef.current !== data.jobId) {
                completedNotifiedRef.current = data.jobId!
                if (Array.isArray(data.warnings) && data.warnings.length > 0) {
                    toast.success('Foundation import complete — review warnings in progress view')
                } else {
                    toast.success('Foundation profile import complete')
                }
            }
            onImportComplete?.()
        } else if (message.name === 'foundation-import-failed') {
            setJobId(data.jobId!)
            setProgress(prev => ({
                ...(prev || { jobId: data.jobId!, status: 'failed' }),
                status: 'failed',
                step: data.step,
                error: data.error,
            }))
            toast.error(data.error || 'Import failed')
        }
    })

    const openModal = useCallback((tab: PasteImportTab = 'guide') => {
        setInitialTab(tab)
        setIsOpen(true)
    }, [])

    const closeModal = useCallback(() => {
        setIsOpen(false)
    }, [])

    const setImportSession = useCallback(
        (nextJobId: string, nextProgress: FoundationPasteImportProgress, credits?: number | null) => {
            setJobId(nextJobId)
            setProgress(nextProgress)
            setEstimatedCredits(credits ?? null)
            setBannerDismissed(false)
            completedNotifiedRef.current = null
        },
        []
    )

    const updateProgress = useCallback((next: FoundationPasteImportProgress) => {
        setProgress(next)
    }, [])

    const dismissBanner = useCallback(() => {
        setBannerDismissed(true)
    }, [])

    const clearImportSession = useCallback(async () => {
        if (!runId) return
        await foundationPasteImportService.clearImportSession(runId)
        setJobId(null)
        setProgress(null)
        setEstimatedCredits(null)
        setBannerDismissed(true)
        completedNotifiedRef.current = null
    }, [runId])

    const recoverStuckImport = useCallback(async () => {
        if (!runId) return
        const res = await foundationPasteImportService.recoverStuckImport(runId)
        if (res.progress) {
            setProgress(res.progress)
            if (res.progress.jobId) setJobId(res.progress.jobId)
        }
        if (res.progress?.status === 'complete') {
            onImportComplete?.()
            toast.success('Import finished — communication brief saved')
        }
    }, [runId, onImportComplete])

    const isImportActive = isActivePasteImportStatus(progress?.status)
    const showBanner =
        !bannerDismissed &&
        progress != null &&
        (isImportActive || progress.status === 'complete' || progress.status === 'failed')

    const value = useMemo(
        () => ({
            isOpen,
            openModal,
            closeModal,
            initialTab,
            progress,
            jobId,
            estimatedCredits,
            setImportSession,
            updateProgress,
            dismissBanner,
            isImportActive,
            showBanner,
            syncFromRunMetadata,
            clearImportSession,
            recoverStuckImport,
        }),
        [
            isOpen,
            openModal,
            closeModal,
            initialTab,
            progress,
            jobId,
            estimatedCredits,
            setImportSession,
            updateProgress,
            dismissBanner,
            isImportActive,
            showBanner,
            syncFromRunMetadata,
            clearImportSession,
            recoverStuckImport,
        ]
    )

    return (
        <FoundationPasteImportContext.Provider value={value}>{children}</FoundationPasteImportContext.Provider>
    )
}

// eslint-disable-next-line react-refresh/only-export-components
export function useFoundationPasteImport(): FoundationPasteImportContextValue {
    const ctx = useContext(FoundationPasteImportContext)
    if (!ctx) {
        throw new Error('useFoundationPasteImport must be used within FoundationPasteImportProvider')
    }
    return ctx
}

/** Safe hook for optional provider (e.g. tests). */
// eslint-disable-next-line react-refresh/only-export-components
export function useFoundationPasteImportOptional(): FoundationPasteImportContextValue | null {
    return useContext(FoundationPasteImportContext)
}
